import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useGetProposalTimelineQuery } from "work/proposals/negotiation/proposal-collaboration-api";
import type { ProposalTimelineEntry } from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalCollectionPathFromConflictKey } from "work/proposals/negotiation/proposal-collection-item-revert";
import { proposalTimelinePathForField } from "work/proposals/negotiation/proposal-field-timeline-path";
import { proposalTimelineEntryIsContentChange } from "work/proposals/negotiation/proposal-timeline";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * The items a negotiated collection no longer has, read out of the collection's own history.
 *
 * An item that was removed used to survive in exactly one place: the whole-collection change history,
 * as a "Removed …" line. Reading that list beside each item's own history was the confusion #4951 and
 * #4954 take away, and taking the list away would have taken the removed items with it — an item that is
 * not in the collection has no row, and so no history control to open. So a collection keeps them as
 * rows of its own: struck through, read-only, each with the history of the item it was.
 *
 * Which items those are is the stream's answer, not the editor's: an item whose newest content event is
 * a removal, and which the collection does not currently list. The removal event carries the whole item
 * as it stood when it left (`CollectionItemRemoved`'s expected value), which is what the row draws. An
 * item removed and later added back reads as present, because its newest event is the add; one still
 * open for undo in the editor is already a row of the redline and is left to it.
 */
export type ProposalRemovedCollectionItem = {
  /** The item's identity in the negotiation stream. */
  itemId: string;
  /** The item as it stood when it was removed. */
  value: Record<string, unknown>;
  /** The removal's place in the stream, so the rows read in the order the items left. */
  sequence: number;
};

const NO_REMOVED_ITEMS: readonly ProposalRemovedCollectionItem[] = [];

/**
 * The items of `collectionPath` whose newest event in `entries` is a removal and whose id is not in
 * `presentItemIds`, oldest removal first. Ids are compared case-insensitively, as every identity
 * comparison in the negotiation is: an id's casing depends on which layer serialized it.
 */
export function removedProposalCollectionItems(
  entries: readonly ProposalTimelineEntry[],
  collectionPath: string,
  presentItemIds: Iterable<string>,
): ProposalRemovedCollectionItem[] {
  const present = new Set([...presentItemIds].map((itemId) => itemId.toLowerCase()));
  const newestByItemId = new Map<string, ProposalTimelineEntry>();

  for (const entry of entries) {
    if (!entry.itemId || !proposalTimelineEntryIsContentChange(entry.kind)) continue;
    // An entry's path is optional on the wire; its conflict key was built from the collection path, so it
    // can place the entry when the path is absent (the same reading `proposalFeeScheduleTimelineLeaf` uses).
    const entryPath = entry.path ?? proposalCollectionPathFromConflictKey(entry.conflictKey, entry.itemId);
    if (entryPath !== collectionPath) continue;

    const key = entry.itemId.toLowerCase();
    const newest = newestByItemId.get(key);
    if (!newest || entry.sequence > newest.sequence) newestByItemId.set(key, entry);
  }

  const removed: ProposalRemovedCollectionItem[] = [];
  for (const [key, entry] of newestByItemId) {
    if (entry.kind !== "CollectionItemRemoved" || present.has(key)) continue;
    // A payload this build cannot read (an older event without the whole item) yields no row rather
    // than a broken one.
    if (!entry.expectedValueExists || !isRecord(entry.expectedValue)) continue;
    removed.push({ itemId: entry.itemId!, value: entry.expectedValue, sequence: entry.sequence });
  }
  return removed.sort((left, right) => left.sequence - right.sequence);
}

/**
 * The removed items of the collection at `collectionPath` under `field`, for a surface that already
 * lists the `presentItemIds`. Reads the field's own history — the query the whole-field history control
 * used to run on demand — so it costs one request per envelope, refetched with every save. Asks for
 * nothing until the negotiation stream exists.
 */
export function useRemovedProposalCollectionItems(args: {
  negotiationId?: string;
  viewerScope?: string;
  field: ProposalField;
  collectionPath: string;
  presentItemIds: readonly string[];
}): readonly ProposalRemovedCollectionItem[] {
  const { negotiationId, viewerScope, field, collectionPath, presentItemIds } = args;
  const path = proposalTimelinePathForField(field);
  const timelineQuery = useGetProposalTimelineQuery(
    negotiationId && viewerScope ? { negotiationId, viewerScope, path } : skipToken,
  );
  const entries = timelineQuery.data?.entries;
  return useMemo(
    () => (entries?.length ? removedProposalCollectionItems(entries, collectionPath, presentItemIds) : NO_REMOVED_ITEMS),
    [collectionPath, entries, presentItemIds],
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
